import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Loader2, CheckCheck } from "lucide-react";
import LeftSidebar from "components/app/LeftSidebar";
import { getAllNotifications, readNotifications } from "reducer/notificationSlice";
import { acceptOrRejectEmailRequest } from "reducer/requestEmailSlice";
import { getTimeAgo, serverFiles } from "utils/utils";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/** Normalize legacy Title Case alert copy → sentence case after the actor name. */
const formatAlertMessage = (message, actorName) => {
  const msg = String(message || "").trim();
  if (!msg) return "";

  const name = String(actorName || "").trim();
  if (name) {
    const lowerMsg = msg.toLowerCase();
    const lowerName = name.toLowerCase();
    if (lowerMsg.startsWith(lowerName)) {
      const action = msg.slice(name.length).trim();
      if (!action) return name;
      return `${name} ${action.toLowerCase()}`.replace(/\s+/g, " ").trim();
    }
  }

  return msg
    .replace(/\bLiked Your Post\b/gi, "liked your post")
    .replace(/\bCommented On Your Post\b/gi, "commented on your post")
    .replace(/\bApplied To Your Brief\b/gi, "applied to your brief")
    .replace(/\bReplied To Your Comment\b/gi, "replied to your comment")
    .replace(/\bStarted Following You\b/gi, "started following you")
    .replace(/\bHas Requested Your Email\b/gi, "has requested your email")
    .replace(/\bHas Accepted Your Email Request\b/gi, "has accepted your email request")
    .replace(/\bHas Rejected Your Email Request\b/gi, "has rejected your email request");
};

const NotificationsPage = ({ setShowRedDot, onClose, embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications, isLoading } = useSelector(
    (state) => state.notifications
  );
  const { userProfile } = useSelector((state) => state.userProfile);
  const myAlias =
    userProfile?.userAliasId ||
    userProfile?.companyResponseDto?.companyAliasId;

  const visibleNotifications = useMemo(() => {
    return (notifications || []).filter((n) => {
      const actorAlias = n?.actionUserDto?.actionUserAliasId;
      // Hide self-triggered noise (e.g. liking your own post on older API)
      if (myAlias && actorAlias && actorAlias === myAlias) return false;
      return true;
    });
  }, [notifications, myAlias]);

  useEffect(() => {
    dispatch(getAllNotifications({ page: 0, size: 50 }));
  }, [dispatch]);

  useEffect(() => {
    if (!visibleNotifications?.length) return;
    const ids = visibleNotifications.filter((n) => !n?.isRead).map((n) => n.id);
    if (ids.length) {
      dispatch(readNotifications(ids));
      setShowRedDot?.(false);
    }
  }, [visibleNotifications, dispatch, setShowRedDot]);

  const updateStatus = async (status, aliasId) => {
    await dispatch(
      acceptOrRejectEmailRequest({ userAliasId: aliasId, status })
    );
    dispatch(getAllNotifications({ page: 0, size: 50 }));
  };

  const hasUnread = useMemo(
    () => visibleNotifications.some((n) => !n?.isRead),
    [visibleNotifications]
  );

  const targetFor = (n) => {
    if (n?.actiontype === "LIKE" || n?.actiontype === "COMMENT") {
      return n?.postId ? `/posts/${n.postId}` : "/feed";
    }
    if (n?.actiontype === "JOB" || n?.actiontype === "APPLICATION") {
      return "/my-applications";
    }
    const alias = n?.actionUserDto?.actionUserAliasId;
    return alias ? `/user-profile/${alias}` : null;
  };

  const openNotification = (n) => {
    const to = targetFor(n);
    onClose?.();
    if (to) navigate(to);
  };

  const content = (
    <section className={embedded ? "" : "lg:col-span-9 min-w-0"}>
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          {!embedded && (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8b83ff] font-medium mb-2">
                Activity
              </div>
              <h1 className="font-display font-bold text-4xl tracking-tighter text-zinc-50">
                Notifications
              </h1>
            </>
          )}
          {embedded && (
            <p className="text-sm text-zinc-400">
              Recent likes, comments, follows and requests.
            </p>
          )}
        </div>
        {hasUnread && (
          <button
            type="button"
            onClick={() => {
              const ids = visibleNotifications
                .filter((n) => !n?.isRead)
                .map((n) => n.id);
              if (ids.length) dispatch(readNotifications(ids));
              setShowRedDot?.(false);
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#8b83ff] hover:text-[#6C63F5]"
          >
            <CheckCheck size={14} /> Mark all read
          </button>
        )}
      </div>

      {isLoading && !visibleNotifications?.length ? (
        <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : !visibleNotifications?.length ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 py-16 text-center text-zinc-500">
          <p>No notifications yet</p>
          <button
            type="button"
            onClick={() => {
              onClose?.();
              navigate("/feed");
            }}
            className="mt-4 inline-flex rounded-full bg-[#6C63F5] text-white px-4 py-2 text-sm font-semibold hover:bg-[#5751E5]"
          >
            Go to Feed
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleNotifications.map((n, i) => {
            const actor = n?.actionUserDto || {};
            const name =
              [actor?.firstName, actor?.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() || "Someone";
            const avatar = actor?.actionUserProfileUrl
              ? serverFiles(actor.actionUserProfileUrl)
              : null;
            const to = targetFor(n);
            const alertText = formatAlertMessage(n?.message, name);

            return (
              <motion.div
                key={n?.id || i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.02 }}
                className={`rounded-2xl border p-4 transition-colors ${
                  n?.isRead
                    ? "border-zinc-800 bg-zinc-900/30"
                    : "border-[#6C63F5]/30 bg-[#6C63F5]/[0.06]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => openNotification(n)}
                  className={`w-full text-left flex items-center gap-4 ${
                    to ? "cursor-pointer" : "cursor-default"
                  }`}
                >
                  <div className="shrink-0">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt=""
                        className="h-11 w-11 rounded-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = NO_IMAGE;
                        }}
                      />
                    ) : (
                      <div className="avatar-fallback h-11 w-11 rounded-full flex items-center justify-center font-semibold text-sm">
                        {initials(name)}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-zinc-100 leading-snug">
                      {alertText}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">
                      {getTimeAgo(n?.createdAt)}
                    </p>
                  </div>
                </button>

                {n?.actiontype === "CONNECT" &&
                  n?.connectionStatus !== "ACCEPT" &&
                  n?.connectionStatus !== "REJECT" && (
                    <div className="mt-3 ml-[60px] flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          updateStatus("ACCEPT", actor?.actionUserAliasId)
                        }
                        className="rounded-full bg-[#6C63F5] text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-[#5751E5]"
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          updateStatus("REJECT", actor?.actionUserAliasId)
                        }
                        className="rounded-full border border-zinc-700 text-zinc-300 px-3.5 py-1.5 text-xs font-semibold hover:border-zinc-500"
                      >
                        Reject
                      </button>
                    </div>
                  )}
              </motion.div>
            );
          })}
        </div>
      )}
    </section>
  );

  if (embedded) return content;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
      <aside className="hidden lg:flex lg:col-span-3 flex-col gap-5 self-start sticky top-24">
        <LeftSidebar />
      </aside>
      {content}
    </div>
  );
};

export default NotificationsPage;
